/**
 * Audit Log Page (US-16-GR-23)
 *
 * Queries the Receipt audit trail via GET /api/Audit/Activity with the server's
 * real filters (dateFrom/dateTo/actorType/category/actorId/aiActionsOnly) and
 * downloads CSV via /Activity/Export.
 */

import { useCallback, useEffect, useState, startTransition } from 'react';
import { RefreshCw, AlertCircle, History, Download } from 'lucide-react';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { Table, type Column } from '../components/Table';
import {
  queryActivityLog,
  exportActivityLogCsv,
  ActorType,
  ActivityCategory,
  ACTOR_TYPE_LABELS,
  ACTIVITY_CATEGORY_LABELS,
  type ActivityLogEntry,
  type ActivityLogFilter,
} from '../services/auditLogService';

const ACTOR_TYPE_OPTIONS = Object.values(ActorType).filter(
  (v): v is ActorType => typeof v === 'number',
);
const CATEGORY_OPTIONS = Object.values(ActivityCategory).filter(
  (v): v is ActivityCategory => typeof v === 'number',
);

const PAGE_SIZE = 100;

interface FilterState {
  dateFrom: string;
  dateTo: string;
  actorType: ActorType | '';
  category: ActivityCategory | '';
  actorId: string;
  aiActionsOnly: boolean;
}

const EMPTY_FILTER: FilterState = {
  dateFrom: '',
  dateTo: '',
  actorType: '',
  category: '',
  actorId: '',
  aiActionsOnly: false,
};

function toApiFilter(f: FilterState, offset: number): ActivityLogFilter {
  return {
    dateFrom: f.dateFrom || undefined,
    dateTo: f.dateTo || undefined,
    actorType: f.actorType === '' ? undefined : f.actorType,
    category: f.category === '' ? undefined : f.category,
    actorId: f.actorId.trim() || undefined,
    aiActionsOnly: f.aiActionsOnly || undefined,
    limit: PAGE_SIZE,
    offset,
  };
}

function fmtTimestamp(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function AuditLogPage() {
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTER);
  // The filters actually reflected in `entries` on screen — updated only on
  // Apply / Reset, never on every keystroke. Export (and paging) must use
  // this, not the live-edited `filter`, or the CSV can silently diverge from
  // what the table is showing (US-24-GR-26 item 6).
  const [appliedFilter, setAppliedFilter] = useState<FilterState>(EMPTY_FILTER);
  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const beginLoad = useLatestRequest();

  const load = useCallback(
    async (pageToLoad: number, currentFilter: FilterState) => {
      const isCurrent = beginLoad();
      setLoading(true);
      setError(null);
      try {
        const rows = await queryActivityLog(
          toApiFilter(currentFilter, (pageToLoad - 1) * PAGE_SIZE),
        );
        if (!isCurrent()) return;
        setEntries(rows);
        setPage(pageToLoad);
      } catch (err) {
        if (!isCurrent()) return;
        setError(err instanceof Error ? err.message : 'Failed to load audit log');
      } finally {
        if (isCurrent()) setLoading(false);
      }
    },
    [beginLoad],
  );

  useEffect(() => {
    // Initial load only; subsequent loads are user-driven via Apply / paging.
    startTransition(() => {
      void load(1, EMPTY_FILTER);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    setAppliedFilter(filter);
    void load(1, filter);
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      await exportActivityLogCsv(toApiFilter(appliedFilter, 0));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const columns: Column<ActivityLogEntry>[] = [
    {
      key: 'timestamp',
      label: 'Timestamp',
      width: 'w-44',
      render: (r) => <span className="text-zinc-400 whitespace-nowrap">{fmtTimestamp(r.timestamp)}</span>,
    },
    {
      key: 'actorType',
      label: 'Actor',
      render: (r) => (
        <span className="text-zinc-300">
          {ACTOR_TYPE_LABELS[r.actorType] ?? String(r.actorType)}
        </span>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      render: (r) => (
        <span className="text-zinc-300">
          {ACTIVITY_CATEGORY_LABELS[r.category] ?? String(r.category)}
        </span>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      render: (r) => <span className="font-mono text-zinc-200">{r.action}</span>,
    },
    {
      key: 'decisionSummary',
      label: 'Decision',
      render: (r) => (
        <span className="text-zinc-400 block max-w-md truncate" title={r.decisionSummary ?? ''}>
          {r.decisionSummary ?? '—'}
        </span>
      ),
    },
    {
      key: 'confidenceScore',
      label: 'Conf.',
      render: (r) => (
        <span className="text-zinc-400 font-mono">
          {r.confidenceScore != null ? r.confidenceScore.toFixed(2) : '—'}
        </span>
      ),
    },
    {
      key: 'modelUsed',
      label: 'Model',
      render: (r) => <span className="text-zinc-500 text-xs">{r.modelUsed ?? '—'}</span>,
    },
    {
      key: 'tokens',
      label: 'Tokens (in/out)',
      render: (r) => (
        <span className="text-zinc-500 font-mono text-xs">
          {r.promptTokens ?? '—'} / {r.completionTokens ?? '—'}
        </span>
      ),
    },
  ];

  const selectClass =
    'px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500';

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-violet-400" />
          <h1 className="text-lg font-semibold text-zinc-100">Audit Log</h1>
        </div>
        <button
          type="button"
          onClick={() => void handleExport()}
          disabled={exporting}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-200 hover:bg-zinc-700 transition-colors disabled:opacity-50 min-h-[48px]"
        >
          {exporting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4 p-4 bg-zinc-900 border border-zinc-800 rounded-lg">
        <div>
          <label htmlFor="audit-from" className="block text-xs font-medium text-zinc-400 mb-1">
            From
          </label>
          <input
            id="audit-from"
            type="date"
            value={filter.dateFrom}
            onChange={(e) => setFilter((f) => ({ ...f, dateFrom: e.target.value }))}
            className={selectClass}
          />
        </div>
        <div>
          <label htmlFor="audit-to" className="block text-xs font-medium text-zinc-400 mb-1">
            To
          </label>
          <input
            id="audit-to"
            type="date"
            value={filter.dateTo}
            onChange={(e) => setFilter((f) => ({ ...f, dateTo: e.target.value }))}
            className={selectClass}
          />
        </div>
        <div>
          <label htmlFor="audit-actor-type" className="block text-xs font-medium text-zinc-400 mb-1">
            Actor Type
          </label>
          <select
            id="audit-actor-type"
            value={filter.actorType}
            onChange={(e) =>
              setFilter((f) => ({
                ...f,
                actorType: e.target.value === '' ? '' : (Number(e.target.value) as ActorType),
              }))
            }
            className={selectClass}
          >
            <option value="">All actors</option>
            {ACTOR_TYPE_OPTIONS.map((a) => (
              <option key={a} value={a}>
                {ACTOR_TYPE_LABELS[a]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="audit-category" className="block text-xs font-medium text-zinc-400 mb-1">
            Category
          </label>
          <select
            id="audit-category"
            value={filter.category}
            onChange={(e) =>
              setFilter((f) => ({
                ...f,
                category:
                  e.target.value === '' ? '' : (Number(e.target.value) as ActivityCategory),
              }))
            }
            className={selectClass}
          >
            <option value="">All categories</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {ACTIVITY_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="audit-actor-id" className="block text-xs font-medium text-zinc-400 mb-1">
            Actor ID
          </label>
          <input
            id="audit-actor-id"
            type="text"
            value={filter.actorId}
            onChange={(e) => setFilter((f) => ({ ...f, actorId: e.target.value }))}
            placeholder="profile GUID"
            className={`${selectClass} w-56`}
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-400 pb-2">
          <input
            type="checkbox"
            checked={filter.aiActionsOnly}
            onChange={(e) => setFilter((f) => ({ ...f, aiActionsOnly: e.target.checked }))}
            className="w-4 h-4 accent-violet-500"
          />
          AI actions only
        </label>
        <button
          type="button"
          onClick={handleApply}
          disabled={loading}
          className="px-4 py-2 rounded-lg bg-violet-600 text-zinc-950 text-sm font-medium hover:bg-violet-500 transition-colors disabled:opacity-50 min-h-[48px]"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={() => {
            setFilter(EMPTY_FILTER);
            setAppliedFilter(EMPTY_FILTER);
            void load(1, EMPTY_FILTER);
          }}
          className="px-3 py-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          Reset
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-400 flex-1">{error}</p>
        </div>
      )}

      {/* Results */}
      <Table<ActivityLogEntry>
        columns={columns}
        rows={entries}
        keyFn={(r) => r.id}
        isLoading={loading}
        emptyMessage="No Receipt entries match the current filters."
      />

      {/* Simple offset paging — server returns up to PAGE_SIZE rows */}
      <div className="flex items-center justify-between mt-3">
        <span className="text-xs text-zinc-500">
          Page {page}
          {entries.length === PAGE_SIZE ? ' — more may be available' : ''}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void load(page - 1, appliedFilter)}
            disabled={loading || page <= 1}
            className="px-3 py-1 text-xs rounded bg-zinc-800 text-zinc-300 disabled:opacity-40 hover:bg-zinc-700 transition-colors min-h-[48px]"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => void load(page + 1, appliedFilter)}
            disabled={loading || entries.length < PAGE_SIZE}
            className="px-3 py-1 text-xs rounded bg-zinc-800 text-zinc-300 disabled:opacity-40 hover:bg-zinc-700 transition-colors min-h-[48px]"
          >
            Next
          </button>
        </div>
      </div>
    </main>
  );
}

export default AuditLogPage;
